import React, { useState, useEffect } from 'react';
import { FormConfig, ThemeConfig, ContentConfig } from './types';
import { defaultContent } from './defaultContent';
import { defaultTheme } from './defaultTheme';
import { Card, SectionTitle, FormInput, FormSelect, FormTextArea } from './components/UIElements';
import { Member, RegistrationFormData } from './types';

const Dashboard: React.FC = () => {
  const settings = (window as any).nifst_settings || {};
  const rootUrl = settings.root || '/wp-json/nifst/v1/';
  const nonce = settings.nonce || '';
  const [config, setConfig] = useState<FormConfig>(settings.form_config || {});
  const [activeTab, setActiveTab] = useState<'design' | 'content' | 'settings' | 'database'>('settings');
  const [dbSubTab, setDbSubTab] = useState<'members' | 'credentials'>('members');
  const [selectedMemberCredentials, setSelectedMemberCredentials] = useState<Member | null>(null);
  const [credentialStatuses, setCredentialStatuses] = useState<Record<string, Record<string, 'pending' | 'verified' | 'rejected'>>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [members, setMembers] = useState<Member[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [pmproLevels, setPmproLevels] = useState<{ id: number; name: string }[]>([]);
  const [pmproLoading, setPmproLoading] = useState(false);
  const [pmproError, setPmproError] = useState('');

  // Merge with defaults for editing to ensure all fields exist
  const mergedTheme = { ...defaultTheme, ...(config.theme || {}) };
  const mergedContent: ContentConfig = { ...defaultContent, ...(config.content || {}) } as ContentConfig;
  // Deep merge steps/labels for editing
  if (config.content?.steps) mergedContent.steps = { ...defaultContent.steps, ...config.content.steps };
  if (config.content?.labels) mergedContent.labels = { ...defaultContent.labels, ...config.content.labels };
  if (config.content?.wizardSteps) mergedContent.wizardSteps = config.content.wizardSteps;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(rootUrl + 'update-config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': nonce
        },
        body: JSON.stringify(config)
      });
      const result = await response.json();
      if (result.success) {
        alert('Configuration saved!');
      } else {
        alert('Error saving configuration: ' + result.message);
      }
    } catch (e) {
      alert('Network error saving configuration.');
      console.error(e);
    } finally {
      setIsSaving(false);
    }
  };

  const updateConfig = (section: keyof FormConfig, value: any) => {
    setConfig(prev => ({ ...prev, [section]: value }));
  };

  const updateTheme = (key: keyof ThemeConfig, value: string) => {
    setConfig(prev => ({
      ...prev,
      theme: { ...(prev.theme || defaultTheme), [key]: value }
    }));
  };

  const updateContent = (section: keyof ContentConfig, key: string, value: any) => {
    // Handling nested updates
    setConfig(prev => {
      const currentContent = prev.content || defaultContent;
      return {
        ...prev,
        content: {
          ...currentContent,
          [section]: {
            ...(currentContent[section as keyof ContentConfig] as object),
            [key]: value
          }
        }
      };
    });
  };

  const updateStepContent = (stepKey: string, field: 'title' | 'subtitle', value: string) => {
    setConfig(prev => {
      const currentContent = prev.content || defaultContent;
      const currentSteps = currentContent.steps || defaultContent.steps;
      return {
        ...prev,
        content: {
          ...currentContent,
          steps: {
            ...currentSteps,
            [stepKey]: {
              ...currentSteps[stepKey],
              [field]: value
            }
          }
        }
      }
    })
  }

  const updateLabel = (key: string, value: string) => {
    setConfig(prev => {
      const currentContent = prev.content || defaultContent;
      const currentLabels = currentContent.labels || defaultContent.labels;
      return {
        ...prev,
        content: {
          ...currentContent,
          labels: {
            ...currentLabels,
            [key]: value
          }
        }
      }
    })
  }

  const fetchMembers = async () => {
    setLoadingMembers(true);
    try {
      const response = await fetch(rootUrl + 'members', {
        headers: { 'X-WP-Nonce': nonce }
      });
      const result = await response.json();
      if (result.success) {
        setMembers(result.members);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMembers(false);
    }
  };

  const fetchPmproLevels = async () => {
    setPmproLoading(true);
    setPmproError('');
    let rawText = '';
    const url = rootUrl + 'pmpro-levels';
    try {
      const response = await fetch(url, {
        headers: { 'X-WP-Nonce': nonce }
      });
      rawText = await response.text();
      const result = JSON.parse(rawText);
      if (result.success) {
        setPmproLevels(result.levels);
        // Sync the category map from server if we don't have one locally
        if (result.category_map && Object.keys(result.category_map).length > 0 && !config.pmpro_map) {
          setConfig(prev => ({ ...prev, pmpro_map: result.category_map }));
        }
      } else {
        setPmproError(result.message || 'Could not load PMPro levels.');
      }
    } catch (e: any) {
      setPmproError('URL: ' + url + ' | Response: ' + rawText.substring(0, 120) + '... | Error: ' + e.message);
    } finally {
      setPmproLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'database') {
      fetchMembers();
    }
    if (activeTab === 'settings') {
      fetchPmproLevels();
    }
  }, [activeTab]);

  const membershipCategories = ['Fellow', 'Professional Member', 'Associate', 'Licentiate', 'Student Member'];

  const updatePmproMap = (category: string, levelId: string) => {
    setConfig(prev => ({
      ...prev,
      pmpro_map: {
        ...(prev.pmpro_map || {}),
        [category]: levelId ? parseInt(levelId) : 0
      }
    }));
  };

  const escCsv = (val: any) => `"${String(val || '').replace(/"/g, '""')}"`;

  const handleExportCSV = () => {
    if (members.length === 0) return;

    const headers = [
      'ID', 'Email', 'Registered Date', 'Category', 'App Type', 'Existing Membership ID',
      'Surname', 'First Name', 'Other Names', 'Sex', 'DOB', 'Marital Status',
      'Office Address', 'Office Phone', 'Office Email',
      'Home Address', 'Postal Address', 'Mobile Phone', 'Home Email',
      'Degrees/Diplomas',
      'Student Course', 'Student Institution', 'Student Commenced', 'Student Completion', 'Student Mode',
      'HOD Certificate URL', 'HOD Signature URL',
      'Employment History', 'Professional Bodies',
      'Career Summary', 'Other Info',
      'Passport Photo URL', 'Applicant Signature URL',
      'Payment Method', 'Payment Reference', 'Payment Status',
      'PMPro Level'
    ];

    const rows = members.map(m => {
      const d = m.data || {} as any;
      return [
        m.id, m.email, m.registered, d.membershipCategory, d.applicationType,
        d.existingMembershipId || '',
        d.surname, d.firstName, d.otherNames, d.sex, d.dob, d.maritalStatus,
        d.officeAddress, d.officeTelephone, d.officeEmail,
        d.homeAddress, d.postalAddress, d.mobileTelephone, d.homeEmail,
        (d.degrees || []).map((deg: any) => `${deg.name || ''} - ${deg.institution || ''} (${deg.yearGraduated || ''})`).join('; '),
        d.studentCourse || '', d.studentInstitution || '', d.studentCommenced || '', d.studentCompletion || '', d.studentMode || '',
        d.hodCertificate || '', d.hodSignature || '',
        d.employmentHistory, d.professionalBodies,
        d.careerSummary, d.otherInfo || '',
        d.passportPhoto || '', d.applicantSignature || '',
        d.paymentMethod, d.paymentReference, d.paymentStatus,
        m.pmpro_level || ''
      ].map(escCsv).join(',');
    });

    const csvContent = "data:text/csv;charset=utf-8,"
      + headers.join(',') + "\n"
      + rows.join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "nifst_members.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportPMProCSV = () => {
    if (members.length === 0) return;

    // PMPro Import format headers (matched to Google Sheet)
    const headers = [
      'user_login', 'user_email', 'user_pass',
      'first_name', 'last_name', 'display_name', 'role',
      'membership_id', 'membership_code_id',
      'membership_initial_payment', 'membership_billing_amount',
      'membership_cycle_number', 'membership_cycle_period', 'membership_billing_limit',
      'membership_trial_amount', 'membership_trial_limit',
      'membership_status', 'membership_startdate', 'membership_enddate',
      'membership_subscription_transaction_id', 'membership_gateway',
      'membership_payment_transaction_id', 'membership_affiliate_id',
      'membership_timestamp'
    ];

    // Get the category → level mapping from config
    const pmproMap: Record<string, number> = (config.pmpro_map as any) || {};
    const appFee = config.application_fee || '5000';

    const rows = members.map(m => {
      const d = m.data || {};
      const levelId = pmproMap[d.membershipCategory] || '';
      const gateway = d.paymentMethod === 'online' ? (config.payment_gateway || 'flutterwave') : '';
      const startDate = m.registered ? m.registered.split(' ')[0] : '';

      return [
        m.email,                                    // user_login
        m.email,                                    // user_email
        '',                                         // user_pass (leave blank, already created)
        d.firstName || '',                          // first_name
        d.surname || '',                            // last_name
        `${d.firstName || ''} ${d.otherNames || ''} ${d.surname || ''}`.replace(/\s+/g, ' ').trim(), // display_name
        'subscriber',                               // role
        levelId,                                    // membership_id
        '',                                         // membership_code_id
        appFee,                                     // membership_initial_payment
        '',                                         // membership_billing_amount
        '',                                         // membership_cycle_number
        '',                                         // membership_cycle_period
        '',                                         // membership_billing_limit
        '',                                         // membership_trial_amount
        '',                                         // membership_trial_limit
        d.paymentStatus === 'completed' ? 'active' : 'inactive', // membership_status
        startDate,                                  // membership_startdate
        '',                                         // membership_enddate
        '',                                         // membership_subscription_transaction_id
        gateway,                                    // membership_gateway
        d.paymentReference || '',                   // membership_payment_transaction_id
        '',                                         // membership_affiliate_id
        startDate                                   // membership_timestamp
      ].map(escCsv).join(',');
    });

    const csvContent = "data:text/csv;charset=utf-8,"
      + headers.join(',') + "\n"
      + rows.join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "nifst_pmpro_import.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-slate-100 p-8 font-sans">
      <div className="max-w-6xl mx-auto">
        <header className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Registration Portal CMS</h1>
            <p className="text-slate-500">Manage form content, design, and settings.</p>
          </div>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-green-600 text-white px-6 py-3 rounded-lg font-bold shadow-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </header>

        <div className="flex gap-6">
          {/* Sidebar */}
          <div className="w-64 flex-shrink-0">
            <nav className="bg-white rounded-xl shadow-sm overflow-hidden">
              <button
                onClick={() => setActiveTab('settings')}
                className={`w-full text-left px-6 py-4 font-medium border-l-4 transition-colors ${activeTab === 'settings' ? 'border-green-500 bg-green-50 text-green-700' : 'border-transparent text-slate-600 hover:bg-slate-50'}`}
              >
                <i className="fa-solid fa-cogs mr-2"></i> General Settings
              </button>
              <button
                onClick={() => setActiveTab('design')}
                className={`w-full text-left px-6 py-4 font-medium border-l-4 transition-colors ${activeTab === 'design' ? 'border-green-500 bg-green-50 text-green-700' : 'border-transparent text-slate-600 hover:bg-slate-50'}`}
              >
                <i className="fa-solid fa-paint-brush mr-2"></i> Design & Style
              </button>
              <button
                onClick={() => setActiveTab('content')}
                className={`w-full text-left px-6 py-4 font-medium border-l-4 transition-colors ${activeTab === 'content' ? 'border-green-500 bg-green-50 text-green-700' : 'border-transparent text-slate-600 hover:bg-slate-50'}`}
              >
                <i className="fa-solid fa-edit mr-2"></i> Form Content
              </button>
              <button
                onClick={() => setActiveTab('database')}
                className={`w-full text-left px-6 py-4 font-medium border-l-4 transition-colors ${activeTab === 'database' ? 'border-green-500 bg-green-50 text-green-700' : 'border-transparent text-slate-600 hover:bg-slate-50'}`}
              >
                <i className="fa-solid fa-database mr-2"></i> Database
              </button>
            </nav>

            <div className="mt-6 bg-white p-4 rounded-xl shadow-sm">
              <h4 className="font-bold text-xs uppercase text-slate-400 mb-2">Shortcode</h4>
              <code className="block bg-slate-100 p-2 rounded text-sm text-slate-700 select-all">
                [nifst_membership_form]
              </code>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 space-y-6">

            {activeTab === 'settings' && (
              <Card>
                <SectionTitle>Form Configuration</SectionTitle>
                <div className="space-y-6">
                  <div className="flex items-center gap-4 p-4 border rounded-lg">
                    <input
                      type="checkbox"
                      className="w-5 h-5 text-green-600"
                      checked={config.enable_employment !== '0'}
                      onChange={e => updateConfig('enable_employment', e.target.checked ? '1' : '0')}
                    />
                    <div>
                      <h4 className="font-bold text-slate-700">Enable Employment History</h4>
                      <p className="text-sm text-slate-500">Show the Employment Record step in the wizard.</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-4 border rounded-lg">
                    <input
                      type="checkbox"
                      className="w-5 h-5 text-green-600"
                      checked={config.enable_referees !== '0'}
                      onChange={e => updateConfig('enable_referees', e.target.checked ? '1' : '0')}
                    />
                    <div>
                      <h4 className="font-bold text-slate-700">Enable Referees</h4>
                      <p className="text-sm text-slate-500">Show the Referees step in the wizard.</p>
                    </div>
                  </div>

                  {/* PMPro Integration */}
                  <div className="border-t pt-6 mt-6">
                    <h3 className="text-lg font-bold text-slate-800 mb-4">
                      <i className="fa-solid fa-id-card mr-2 text-green-500"></i>
                      PMPro Membership Mapping
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Map each NIFST membership category to a Paid Memberships Pro level. New registrants will be automatically enrolled.
                    </p>

                    {pmproError && (
                      <div className="bg-amber-50 border border-amber-300 text-amber-800 px-4 py-3 rounded-lg mb-4">
                        <i className="fa-solid fa-triangle-exclamation mr-2"></i>
                        {pmproError}
                      </div>
                    )}

                    {pmproLoading ? (
                      <p className="text-slate-500">Loading PMPro levels...</p>
                    ) : pmproLevels.length > 0 ? (
                      <div className="space-y-3">
                        {membershipCategories.map(cat => (
                          <div key={cat} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                            <span className="w-48 font-medium text-slate-700">{cat}</span>
                            <i className="fa-solid fa-arrow-right text-slate-400"></i>
                            <select
                              value={(config.pmpro_map as any)?.[cat] || ''}
                              onChange={e => updatePmproMap(cat, e.target.value)}
                              className="flex-1 p-2 border rounded-lg bg-white"
                            >
                              <option value="">— No PMPro Level —</option>
                              {pmproLevels.map(level => (
                                <option key={level.id} value={level.id}>
                                  {level.name} (ID: {level.id})
                                </option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-slate-50 p-6 rounded-lg text-center">
                        <p className="text-slate-500 mb-2">No PMPro levels found.</p>
                        <p className="text-xs text-slate-400">Install and activate Paid Memberships Pro, then create membership levels to enable auto-enrollment.</p>
                      </div>
                    )}
                  </div>

                  {/* Payment Settings */}
                  <div className="border-t pt-6 mt-6">
                    <h3 className="text-lg font-bold text-slate-800 mb-4">
                      <i className="fa-solid fa-credit-card mr-2 text-green-500"></i>
                      Payment Settings
                    </h3>

                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block font-bold text-slate-700 mb-2">Payment Gateway</label>
                          <select
                            value={config.payment_gateway || 'flutterwave'}
                            onChange={e => updateConfig('payment_gateway', e.target.value)}
                            className="w-full p-2 border rounded-lg"
                          >
                            <option value="flutterwave">Flutterwave</option>
                            <option value="paystack">Paystack</option>
                          </select>
                        </div>
                        <div>
                          <label className="block font-bold text-slate-700 mb-2">Application Fee (₦)</label>
                          <input
                            type="number"
                            value={config.application_fee || '5000'}
                            onChange={e => updateConfig('application_fee', e.target.value)}
                            className="w-full p-2 border rounded-lg"
                            placeholder="5000"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block font-bold text-slate-700 mb-2">Public Key</label>
                          <input
                            type="text"
                            value={config.payment_public_key || ''}
                            onChange={e => updateConfig('payment_public_key', e.target.value)}
                            className="w-full p-2 border rounded-lg font-mono text-sm"
                            placeholder="pk_test_..."
                          />
                        </div>
                        <div>
                          <label className="block font-bold text-slate-700 mb-2">Secret Key</label>
                          <input
                            type="password"
                            value={config.payment_secret_key || ''}
                            onChange={e => updateConfig('payment_secret_key', e.target.value)}
                            className="w-full p-2 border rounded-lg font-mono text-sm"
                            placeholder="sk_test_..."
                          />
                          <p className="text-xs text-slate-500 mt-1">Used server-side for payment verification only.</p>
                        </div>
                      </div>

                      <div className="bg-slate-50 p-4 rounded-lg border">
                        <h4 className="font-bold text-slate-700 mb-3">Offline Payment Bank Details</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-600 mb-1">Bank Name</label>
                            <input
                              type="text"
                              value={config.bank_name || ''}
                              onChange={e => updateConfig('bank_name', e.target.value)}
                              className="w-full p-2 border rounded-lg"
                              placeholder="e.g. First Bank"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-600 mb-1">Account Number</label>
                            <input
                              type="text"
                              value={config.bank_account_number || ''}
                              onChange={e => updateConfig('bank_account_number', e.target.value)}
                              className="w-full p-2 border rounded-lg font-mono"
                              placeholder="0123456789"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-600 mb-1">Account Name</label>
                            <input
                              type="text"
                              value={config.bank_account_name || ''}
                              onChange={e => updateConfig('bank_account_name', e.target.value)}
                              className="w-full p-2 border rounded-lg"
                              placeholder="NIFST"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {activeTab === 'design' && (
              <Card>
                <SectionTitle>Design Customization</SectionTitle>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="block font-bold text-slate-700 mb-2">Primary Color</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={mergedTheme.primaryColor}
                        onChange={e => updateTheme('primaryColor', e.target.value)}
                        className="h-10 w-20 rounded cursor-pointer"
                      />
                      <span className="text-slate-600">{mergedTheme.primaryColor}</span>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">Used for buttons, active steps, and highlights.</p>
                  </div>

                  <div>
                    <label className="block font-bold text-slate-700 mb-2">Secondary Color</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={mergedTheme.secondaryColor}
                        onChange={e => updateTheme('secondaryColor', e.target.value)}
                        className="h-10 w-20 rounded cursor-pointer"
                      />
                      <span className="text-slate-600">{mergedTheme.secondaryColor}</span>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">Used for headers and submit buttons.</p>
                  </div>

                  <div>
                    <label className="block font-bold text-slate-700 mb-2">Background Color</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={mergedTheme.backgroundColor}
                        onChange={e => updateTheme('backgroundColor', e.target.value)}
                        className="h-10 w-20 rounded cursor-pointer"
                      />
                      <span className="text-slate-600">{mergedTheme.backgroundColor}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block font-bold text-slate-700 mb-2">Border Radius</label>
                    <select
                      value={mergedTheme.borderRadius}
                      onChange={e => updateTheme('borderRadius', e.target.value)}
                      className="w-full p-2 border rounded-lg"
                    >
                      <option value="0px">Square (0px)</option>
                      <option value="0.25rem">Small (4px)</option>
                      <option value="0.5rem">Medium (8px)</option>
                      <option value="0.75rem">Large (12px)</option>
                      <option value="1rem">Extra Large (16px)</option>
                      <option value="9999px">Rounded (Full)</option>
                    </select>
                  </div>

                  <div className="col-span-2">
                    <label className="block font-bold text-slate-700 mb-2">Font Family</label>
                    <input
                      type="text"
                      value={mergedTheme.fontFamily}
                      onChange={e => updateTheme('fontFamily', e.target.value)}
                      className="w-full p-2 border rounded-lg"
                      placeholder="e.g. Arial, sans-serif"
                    />
                  </div>

                  <div className="col-span-2 border-t pt-4">
                    <label className="block font-bold text-slate-700 mb-2">Logo URL</label>
                    <div className="flex gap-4">
                      <input
                        type="text"
                        value={mergedTheme.logoUrl || ''}
                        onChange={e => updateTheme('logoUrl', e.target.value)}
                        className="flex-1 p-2 border rounded-lg"
                        placeholder="https://example.com/logo.png"
                      />
                      {/* Note: Full media library integration requires React wrapper for wp.media. For now, manual URL entry is supported. */}
                    </div>
                    <p className="text-sm text-slate-500 mt-1">Enter the URL of your logo (upload via WordPress Media Library and copy URL).</p>
                  </div>
                </div>
              </Card>
            )}

            {activeTab === 'content' && (
              <div className="space-y-6">
                <Card>
                  <SectionTitle>Global Content</SectionTitle>
                  <div className="space-y-4">
                    <FormTextArea
                      label="Declaration Text"
                      value={config.declaration_text || mergedContent.declaration}
                      onChange={e => updateConfig('declaration_text', e.target.value)}
                    />
                  </div>
                </Card>

                <Card>
                  <SectionTitle>Step Titles & Subtitles</SectionTitle>
                  <div className="space-y-6">
                    {Object.entries(mergedContent.steps).map(([key, step]) => (
                      <div key={key} className="p-4 border rounded-lg bg-slate-50">
                        <h4 className="font-bold text-slate-700 capitalize mb-2">{key}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormInput
                            label="Title"
                            value={step.title}
                            onChange={e => updateStepContent(key, 'title', e.target.value)}
                          />
                          <FormInput
                            label="Subtitle"
                            value={step.subtitle}
                            onChange={e => updateStepContent(key, 'subtitle', e.target.value)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                <Card>
                  <SectionTitle>Field Labels</SectionTitle>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(mergedContent.labels).map(([key, label]) => (
                      <div key={key}>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{key}</label>
                        <input
                          type="text"
                          value={label}
                          onChange={e => updateLabel(key, e.target.value)}
                          className="w-full p-2 border rounded focus:ring-2 focus:ring-green-500 outline-none"
                        />
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            )}

            {activeTab === 'database' && (() => {
              const memberCategory = selectedMemberCredentials?.data?.membershipCategory || '';
              const isStudentMember = memberCategory === 'Student Member';
              const isFellow = memberCategory === 'Fellow';

              // Credential field definitions — filter by membership category
              const credentialFields: { key: string; label: string; type: 'image' | 'file' }[] = [
                { key: 'passportPhoto', label: 'Passport Photo', type: 'image' },
                // HOD fields only apply to Student Members
                ...(isStudentMember ? [
                  { key: 'hodCertificate', label: 'HOD Certificate', type: 'file' as const },
                  { key: 'hodSignature', label: 'HOD Signature', type: 'image' as const },
                ] : []),
                { key: 'applicantSignature', label: 'Applicant Signature', type: 'image' },
                { key: 'paymentReceipt', label: 'Payment Receipt', type: 'file' },
                // Fellow fields only apply to Fellow applicants
                ...(isFellow ? [
                  { key: 'fellowCitationUpload', label: 'Fellow Citation', type: 'file' as const },
                  { key: 'fellowStatementUpload', label: 'Fellow Statement', type: 'file' as const },
                ] : []),
              ];

              const setCredStatus = (memberId: number, field: string, status: 'pending' | 'verified' | 'rejected') => {
                setCredentialStatuses(prev => ({
                  ...prev,
                  [memberId]: { ...(prev[memberId] || {}), [field]: status }
                }));
              };

              const getCredStatus = (memberId: number, field: string) =>
                credentialStatuses[memberId]?.[field] || 'pending';

              const isUrl = (val: any) => typeof val === 'string' && (val.startsWith('http') || val.startsWith('/'));

              return (
              <>
                {/* Sub-tab bar */}
                <div className="flex gap-0 mb-6 border-b border-slate-200">
                  <button
                    onClick={() => setDbSubTab('members')}
                    className={`px-6 py-3 font-semibold text-sm border-b-2 transition-colors ${
                      dbSubTab === 'members' ? 'border-green-600 text-green-700' : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <i className="fa-solid fa-users mr-2"></i>Registered Members
                  </button>
                  <button
                    onClick={() => setDbSubTab('credentials')}
                    className={`px-6 py-3 font-semibold text-sm border-b-2 transition-colors ${
                      dbSubTab === 'credentials' ? 'border-green-600 text-green-700' : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <i className="fa-solid fa-graduation-cap mr-2"></i>Academy Credentials
                    {members.length > 0 && (
                      <span className="ml-2 bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full font-bold">{members.length}</span>
                    )}
                  </button>
                </div>

                {/* Members sub-tab */}
                {dbSubTab === 'members' && (
                  <Card>
                    <div className="flex justify-between items-center mb-6">
                      <SectionTitle>Registered Members</SectionTitle>
                      <div className="flex gap-2">
                        <button onClick={fetchMembers} className="text-green-600 hover:text-green-800 p-2">
                          <i className="fa-solid fa-sync"></i> Refresh
                        </button>
                        <button onClick={handleExportCSV} disabled={members.length === 0}
                          className="bg-green-600 text-white px-4 py-2 rounded font-bold hover:bg-green-700 disabled:opacity-50">
                          <i className="fa-solid fa-file-excel mr-2"></i> Export CSV
                        </button>
                        <button onClick={handleExportPMProCSV} disabled={members.length === 0}
                          className="bg-purple-600 text-white px-4 py-2 rounded font-bold hover:bg-purple-700 disabled:opacity-50">
                          <i className="fa-solid fa-file-import mr-2"></i> Export for PMPro
                        </button>
                      </div>
                    </div>
                    {loadingMembers ? (
                      <div className="text-center py-12 text-slate-500">
                        <i className="fa-solid fa-spinner fa-spin text-2xl mb-2"></i>
                        <p>Loading members...</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                          <thead>
                            <tr className="bg-slate-50 border-b">
                              <th className="p-3 font-bold text-slate-600">ID</th>
                              <th className="p-3 font-bold text-slate-600">Name</th>
                              <th className="p-3 font-bold text-slate-600">Email</th>
                              <th className="p-3 font-bold text-slate-600">Category</th>
                              <th className="p-3 font-bold text-slate-600">PMPro Level</th>
                              <th className="p-3 font-bold text-slate-600">Registered</th>
                              <th className="p-3 font-bold text-slate-600">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {members.length === 0 ? (
                              <tr><td colSpan={7} className="p-8 text-center text-slate-500">No members found.</td></tr>
                            ) : members.map(member => (
                              <tr key={member.id} className="border-b hover:bg-slate-50">
                                <td className="p-3 text-slate-600">#{member.id}</td>
                                <td className="p-3 font-medium text-slate-800">{member.data.surname} {member.data.otherNames}</td>
                                <td className="p-3 text-slate-600">{member.email}</td>
                                <td className="p-3">
                                  <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-bold">{member.data.membershipCategory}</span>
                                </td>
                                <td className="p-3">
                                  <span className={`text-xs px-2 py-1 rounded-full font-bold ${member.pmpro_level && member.pmpro_level !== 'None' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'}`}>
                                    {member.pmpro_level || 'N/A'}
                                  </span>
                                </td>
                                <td className="p-3 text-slate-500 text-sm">{new Date(member.registered).toLocaleDateString()}</td>
                                <td className="p-3 flex gap-2">
                                  <a href={`${settings.admin_url}user-edit.php?user_id=${member.id}`}
                                    className="text-green-600 hover:text-green-800 font-medium text-sm" target="_blank" rel="noreferrer">
                                    <i className="fa-solid fa-edit mr-1"></i> Edit
                                  </a>
                                  <button
                                    onClick={() => { setDbSubTab('credentials'); setSelectedMemberCredentials(member); }}
                                    className="text-blue-600 hover:text-blue-800 font-medium text-sm ml-2">
                                    <i className="fa-solid fa-graduation-cap mr-1"></i> Credentials
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Card>
                )}

                {/* Credentials sub-tab */}
                {dbSubTab === 'credentials' && (
                  <div className="space-y-6">
                    {/* Member selector */}
                    <Card>
                      <div className="flex items-center gap-4">
                        <label className="font-bold text-slate-700 whitespace-nowrap">Select Member:</label>
                        <select
                          className="flex-1 p-2 border rounded-lg bg-white"
                          value={selectedMemberCredentials?.id || ''}
                          onChange={e => {
                            const m = members.find(x => x.id === parseInt(e.target.value));
                            setSelectedMemberCredentials(m || null);
                          }}
                        >
                          <option value="">— Choose a member —</option>
                          {members.map(m => (
                            <option key={m.id} value={m.id}>
                              #{m.id} — {m.data.firstName} {m.data.surname} ({m.email})
                            </option>
                          ))}
                        </select>
                        {selectedMemberCredentials && (
                          <span className="bg-green-100 text-green-800 text-xs px-3 py-1 rounded-full font-bold whitespace-nowrap">
                            {selectedMemberCredentials.data.membershipCategory}
                          </span>
                        )}
                      </div>
                    </Card>

                    {!selectedMemberCredentials ? (
                      <Card>
                        <div className="text-center py-16 text-slate-400">
                          <i className="fa-solid fa-graduation-cap text-5xl mb-4 block"></i>
                          <p className="text-lg font-medium">Select a member above to view their uploaded credentials</p>
                          <p className="text-sm mt-1">You can verify or reject each document individually</p>
                        </div>
                      </Card>
                    ) : (() => {
                      const d = selectedMemberCredentials.data as any;
                      const memberId = selectedMemberCredentials.id;

                      // Also collect degree/diploma certificates
                      const academicFiles: { label: string; url: string }[] = [];
                      ([...(d.degrees || []), ...(d.diplomas || [])]).forEach((entry: any) => {
                        if (entry?.certificate && isUrl(entry.certificate)) {
                          academicFiles.push({ label: `${entry.name || 'Qualification'} — ${entry.institution || ''}`, url: entry.certificate });
                        }
                      });

                      const statusColor = (s: string) =>
                        s === 'verified' ? 'bg-green-100 text-green-700 border-green-300'
                        : s === 'rejected' ? 'bg-red-100 text-red-700 border-red-300'
                        : 'bg-amber-50 text-amber-700 border-amber-300';

                      return (
                        <>
                          {/* Standard credential files */}
                          <Card>
                            <SectionTitle subtitle={`Member: ${selectedMemberCredentials.data.firstName} ${selectedMemberCredentials.data.surname}`}>
                              Uploaded Documents
                            </SectionTitle>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {credentialFields.map(({ key, label, type }) => {
                                const url = d[key];
                                const status = getCredStatus(memberId, key);
                                if (!url || !isUrl(url)) return (
                                  <div key={key} className="border rounded-lg p-4 bg-slate-50">
                                    <p className="text-sm font-bold text-slate-600 mb-1">{label}</p>
                                    <p className="text-xs text-slate-400 italic">Not uploaded</p>
                                  </div>
                                );
                                return (
                                  <div key={key} className={`border rounded-lg p-4 ${statusColor(status)}`}>
                                    <div className="flex justify-between items-start mb-2">
                                      <p className="text-sm font-bold">{label}</p>
                                      <span className={`text-xs px-2 py-0.5 rounded-full font-bold border capitalize ${
                                        status === 'verified' ? 'bg-green-200 text-green-800 border-green-400'
                                        : status === 'rejected' ? 'bg-red-200 text-red-800 border-red-400'
                                        : 'bg-amber-100 text-amber-800 border-amber-300'
                                      }`}>{status}</span>
                                    </div>
                                    {type === 'image' ? (
                                      <img src={url} alt={label} className="w-full max-h-40 object-contain rounded border bg-white mb-3" />
                                    ) : (
                                      <a href={url} target="_blank" rel="noreferrer"
                                        className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm mb-3">
                                        <i className="fa-solid fa-file-arrow-down"></i> View / Download File
                                      </a>
                                    )}
                                    <div className="flex gap-2 mt-1">
                                      <button
                                        onClick={() => setCredStatus(memberId, key, 'verified')}
                                        className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs font-bold py-1.5 rounded transition-colors">
                                        <i className="fa-solid fa-check mr-1"></i> Verify
                                      </button>
                                      <button
                                        onClick={() => setCredStatus(memberId, key, 'rejected')}
                                        className="flex-1 bg-red-500 hover:bg-red-600 text-white text-xs font-bold py-1.5 rounded transition-colors">
                                        <i className="fa-solid fa-times mr-1"></i> Reject
                                      </button>
                                      <button
                                        onClick={() => setCredStatus(memberId, key, 'pending')}
                                        className="bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-bold py-1.5 px-2 rounded transition-colors">
                                        Reset
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </Card>

                          {/* Academic certificates from degrees/diplomas */}
                          {academicFiles.length > 0 && (
                            <Card>
                              <SectionTitle>Academic Certificates</SectionTitle>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {academicFiles.map((af, i) => {
                                  const key = `academic_${i}`;
                                  const status = getCredStatus(memberId, key);
                                  const isPdf = af.url.toLowerCase().endsWith('.pdf');
                                  return (
                                    <div key={key} className={`border rounded-lg p-4 ${statusColor(status)}`}>
                                      <div className="flex justify-between items-start mb-2">
                                        <p className="text-sm font-bold truncate pr-2">{af.label}</p>
                                        <span className={`text-xs px-2 py-0.5 rounded-full font-bold border capitalize whitespace-nowrap ${
                                          status === 'verified' ? 'bg-green-200 text-green-800 border-green-400'
                                          : status === 'rejected' ? 'bg-red-200 text-red-800 border-red-400'
                                          : 'bg-amber-100 text-amber-800 border-amber-300'
                                        }`}>{status}</span>
                                      </div>
                                      {isPdf ? (
                                        <a href={af.url} target="_blank" rel="noreferrer"
                                          className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm mb-3">
                                          <i className="fa-solid fa-file-pdf"></i> View Certificate (PDF)
                                        </a>
                                      ) : (
                                        <img src={af.url} alt={af.label} className="w-full max-h-40 object-contain rounded border bg-white mb-3" />
                                      )}
                                      <div className="flex gap-2 mt-1">
                                        <button onClick={() => setCredStatus(memberId, key, 'verified')}
                                          className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs font-bold py-1.5 rounded">
                                          <i className="fa-solid fa-check mr-1"></i> Verify
                                        </button>
                                        <button onClick={() => setCredStatus(memberId, key, 'rejected')}
                                          className="flex-1 bg-red-500 hover:bg-red-600 text-white text-xs font-bold py-1.5 rounded">
                                          <i className="fa-solid fa-times mr-1"></i> Reject
                                        </button>
                                        <button onClick={() => setCredStatus(memberId, key, 'pending')}
                                          className="bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-bold py-1.5 px-2 rounded">
                                          Reset
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </Card>
                          )}

                          {/* Summary banner */}
                          <Card>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-bold text-slate-700 mb-1">Verification Summary</p>
                                <div className="flex gap-4 text-sm">
                                  <span className="text-green-700">
                                    <i className="fa-solid fa-check-circle mr-1"></i>
                                    {Object.values(credentialStatuses[memberId] || {}).filter(s => s === 'verified').length} Verified
                                  </span>
                                  <span className="text-red-600">
                                    <i className="fa-solid fa-times-circle mr-1"></i>
                                    {Object.values(credentialStatuses[memberId] || {}).filter(s => s === 'rejected').length} Rejected
                                  </span>
                                  <span className="text-amber-600">
                                    <i className="fa-solid fa-clock mr-1"></i> Pending review
                                  </span>
                                </div>
                              </div>
                              <a
                                href={`${settings.admin_url}user-edit.php?user_id=${memberId}`}
                                target="_blank" rel="noreferrer"
                                className="bg-slate-700 text-white text-sm px-4 py-2 rounded hover:bg-slate-800 font-bold">
                                <i className="fa-solid fa-user-edit mr-2"></i>Edit Member Profile
                              </a>
                            </div>
                          </Card>
                        </>
                      );
                    })()}
                  </div>
                )}
              </>
              );
            })()}


          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
